<template>
  <div class="player-info">
    <share
      v-show="showShareOverlay"
      :shown="showShareOverlay"
      :is-classic="isClassic"
      :url="url"
      :title="title"
      :description="description"
      @close="closeShareOverlay"
    ></share>
    <modal
      v-if="optionsModalActive"
      background-color="rgba(0, 0, 0, 0.5)"
      centered
      :width="304"
      label="Player options"
      class="autoplay"
      @close="closeOptionsModal"
    >
      <template #header>
        <div class="autoplay__header">
          <h2 class="autoplay__heading">Autoplay</h2>
          <button
            class="autoplay__button"
            :class="{ 'autoplay__button--active': autoplayActive }"
            :aria-pressed="autoplayActive ? 'true' : 'false'"
            @click="toggleAutoplay"
          >
            <!-- 🔴 D-239: this was the ONE pre-existing `aria-pressed` in the tree, and
                 the plan's first draft cited it as precedent. It CONTRADICTED ITSELF.
                 The name interpolated a `nextAutoplayAction` computed that returned
                 the OPPOSITE of the state — so with autoplay ON, AT said
                 "Toggle autoplay Off … pressed". Two claims, one true. That computed had
                 no other reader and is deleted with it, rather than left dead. -->
            <!-- (The moustache is deliberately not spelled out above, so a grep for the
                 deleted computed's name finds no false hit.)

                 🔑 Pick ONE mechanism. `aria-pressed` is right here — autoplay is a
                 genuine sticky state, not a changing action — so the state lives in the
                 attribute and the NAME goes static. This is the opposite call from
                 `PlayPause`, whose `action` computed correctly changes the name because
                 its ACTION changes; "Play, pressed" is unresolvable. D-239 records both
                 halves so the next reader does not copy the wrong one. -->
            <span class="is-sr-only">Autoplay</span>
            <span
              class="autoplay__button-toggle"
              :class="{ 'autoplay__button-toggle--active': autoplayActive }"
              aria-hidden="true"
            >
              <span v-show="autoplayActive" v-html="checkmarkSvg"></span>
            </span>
          </button>
        </div>
      </template>
    </modal>
    <div class="player-info__content">
      <p
        class="player-info__text player-info__text--title"
        :class="titleClass"
        :style="themeStyles"
        ref="title"
      >
        {{ computedTitle }}
      </p>
      <div class="player-info__row">
        <p
          v-if="!isLiveStream"
          class="player-info__text player-info__text--light"
          :style="themeStyles"
        >
          {{ showTitle }}
        </p>
        <p
          v-if="!isLiveStream"
          class="player-info__text player-info__text--light"
          :style="themeStyles"
        >
          {{ unixToDateFormat(publishDate) }}
        </p>
        <span
          class="player-info__live-text"
          v-if="isLiveStream"
          :style="themeStylesForLiveText"
        >
          LIVE
        </span>
        <ul class="player-info__list">
          <li class="player-info__list-item">
            <button class="player-info__button" @click="openShareOverlay">
              <span class="is-sr-only">Share this podcast</span>
              <span v-html="shareSvg"></span>
            </button>
          </li>
          <li v-if="showOptions" class="player-info__list-item">
            <button class="player-info__button" @click="openOptionsModal">
              <span class="is-sr-only">Open options</span>
              <span v-html="gearSvg"></span>
            </button>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { formatMonthDayYear } from '../utils/dateFormat';
import Modal from './Utils/Modal';
import Share from './Share';

export default {
  mounted() {
    window.addEventListener('resize', this.scrollTitle);
    this.scrollTitle();
  },
  unmounted() {
    window.removeEventListener('resize', this.scrollTitle);
  },
  name: 'player-info',
  emits: ['toggle-autoplay'],
  props: {
    title: {
      type: String,
      default: '',
    },
    showTitle: {
      type: String,
      default: '',
    },
    description: String,
    url: String,
    publishDate: {
      type: Number,
      default: NaN,
    },
    theme: {
      type: Object,
      validator(value) {
        return ['light', 'dark', 'custom'].includes(value.type);
      },
    },
    isPlaying: {
      type: Boolean,
      default() {
        return false;
      },
    },
    isClassic: {
      type: Boolean,
      default() {
        return false;
      },
    },
    autoplayActive: {
      type: Boolean,
      default() {
        return true;
      },
    },
    showOptions: {
      type: Boolean,
      default() {
        return false;
      },
    },
    isLiveStream: {
      type: Boolean,
      default() {
        return false;
      },
    },
  },
  components: {
    Modal,
    Share,
  },
  data() {
    return {
      showShareOverlay: false,
      titleOverflows: false,
      optionsModalActive: false,
    };
  },
  computed: {
    computedTitle() {
      return this.isLiveStream
        ? `Listening to ${this.showTitle.toUpperCase()}`
        : this.title;
    },

    shareSvg() {
      return `
        <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M13 11.9C12.3667 11.9 11.8 12.15 11.3667 12.5417L5.425 9.08333C5.46667 8.89166 5.5 8.7 5.5 8.5C5.5
            8.3 5.46667 8.10833 5.425 7.91666L11.3 4.49166C11.75 4.90833 12.3417 5.16666 13 5.16666C14.3833 5.16666 15.5
            4.05 15.5 2.66666C15.5 1.28333 14.3833 0.166664 13 0.166664C11.6167 0.166664 10.5 1.28333 10.5 2.66666C10.5
            2.86666 10.5333 3.05833 10.575 3.25L4.7 6.675C4.25 6.25833 3.65833 6 3 6C1.61667 6 0.5 7.11666 0.5 8.5C0.5
            9.88333 1.61667 11 3 11C3.65833 11 4.25 10.7417 4.7 10.325L10.6333 13.7917C10.5917 13.9667 10.5667 14.15
            10.5667 14.3333C10.5667 15.675 11.6583 16.7667 13 16.7667C14.3417 16.7667 15.4333 15.675 15.4333
            14.3333C15.4333 12.9917 14.3417 11.9 13 11.9Z"
            fill="${this.theme.text}"
          />
        </svg>
      `;
    },

    gearSvg() {
      return `
        <svg width="18" height="17" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M15.1917 9.31663C15.225 9.04996 15.25 8.78329 15.25 8.49996C15.25 8.21663 15.225 7.94996 15.1917
            7.68329L16.95 6.30829C17.1083 6.18329 17.15 5.95829 17.05 5.77496L15.3833 2.89163C15.2833 2.70829 15.0583
            2.64163 14.875 2.70829L12.8 3.54163C12.3667 3.20829 11.9 2.93329 11.3917 2.72496L11.075 0.516626C11.05
            0.316626 10.875 0.166626 10.6667 0.166626H7.33332C7.12499 0.166626 6.94999 0.316626 6.92499 0.516626L6.60832
            2.72496C6.09999 2.93329 5.63332 3.21663 5.19999 3.54163L3.12499 2.70829C2.93332 2.63329 2.71666 2.70829
            2.61666 2.89163L0.949988 5.77496C0.841655 5.95829 0.891655 6.18329 1.04999 6.30829L2.80832 7.68329C2.77499
            7.94996 2.74999 8.22496 2.74999 8.49996C2.74999 8.77496 2.77499 9.04996 2.80832 9.31663L1.04999
            10.6916C0.891655 10.8166 0.849988 11.0416 0.949988 11.225L2.61666 14.1083C2.71666 14.2916 2.94165 14.3583
            3.12499 14.2916L5.19999 13.4583C5.63332 13.7916 6.09999 14.0666 6.60832 14.275L6.92499 16.4833C6.94999
            16.6833 7.12499 16.8333 7.33332 16.8333H10.6667C10.875 16.8333 11.05 16.6833 11.075 16.4833L11.3917
            14.275C11.9 14.0666 12.3667 13.7833 12.8 13.4583L14.875 14.2916C15.0667 14.3666 15.2833 14.2916 15.3833
            14.1083L17.05 11.225C17.15 11.0416 17.1083 10.8166 16.95 10.6916L15.1917 9.31663ZM8.99999 11.4166C7.39165
            11.4166 6.08332 10.1083 6.08332 8.49996C6.08332 6.89163 7.39165 5.58329 8.99999 5.58329C10.6083 5.58329
            11.9167 6.89163 11.9167 8.49996C11.9167 10.1083 10.6083 11.4166 8.99999 11.4166Z"
            fill="${this.theme.text}"
          />
        </svg>
      `;
    },

    checkmarkSvg() {
      return `
        <svg width="18" height="14" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M6.00016 11.17L1.83016 7L0.410156 8.41L6.00016 14L18.0002 2L16.5902 0.589996L6.00016 11.17Z"
            fill="white"
          />
        </svg>
      `;
    },

    // Vue OWNS this class list. It used to return only the `--reset` modifier while
    // `scrollTitle()` added `player-info__text-scroll` imperatively — and because Vue
    // rewrites className whenever the bound value CHANGES, the state update that
    // recorded the overflow wiped the class added in that same tick. Vue 1 ignored
    // the binding, so master never showed it (Phase 2).
    //
    // Both classes must render together: `&--reset` supplies only `animation-name`,
    // while duration/iteration/timing/fill-mode all live on `player-info__text-scroll`.
    // With only the modifier there is no animation-duration, so NOTHING animates —
    // neither the marquee while playing nor the reset while paused.
    titleClass() {
      return {
        'player-info__text-scroll': this.titleOverflows,
        'player-info__text-scroll--reset': this.titleOverflows && !this.isPlaying,
      };
    },

    themeStyles() {
      return `color: ${this.theme.text};`;
    },

    themeStylesForLiveText() {
      // we'll defer to theme colors
      // when not using light theme
      if (this.theme.type !== 'light') {
        return {
          color: this.theme.text,
        };
      }

      return null;
    },
  },
  methods: {
    scrollTitle() {
      this.$nextTick(() => {
        const title = this.$refs.title;

        // Reset. `titleOverflows = false` is what removes the marquee classes now
        // — see titleClass. Touching classList here would fight the renderer for
        // ownership of one attribute, which is the bug this replaced.
        this.titleOverflows = false;
        title.style.width = 'auto';

        // 🔴 ...but that removal is REACTIVE, so it has not been applied yet, and
        // the measurement below must not run against a live marquee. The original
        // code removed the class SYNCHRONOUSLY and measured immediately; replacing
        // that with a reactive write silently changed the measurement conditions
        // — MEASURED: at the moment `parent.scrollWidth` was read, className still
        // contained `player-info__text-scroll`. A running `marqueeText` translates
        // the title out to +100%, and a transformed box contributes to its
        // ancestor's scrollable overflow, so the width read back is a moving
        // number rather than the text's natural one.
        // A second tick lets Vue actually apply the removal first. Found by the
        // review of this very fix.
        this.$nextTick(() => {
          const parent = title.parentNode;
          if (parent.scrollWidth > title.offsetWidth) {
          // Need to be explicit about the width for the scroll to function properly
          // Else the width will be set to the parent container's width
            title.setAttribute(
              'style',
              `width: ${parent.scrollWidth}px; ${this.themeStyles}`
            );
            this.titleOverflows = true;
          }
        });
      });
    },

    openShareOverlay() {
      this.showShareOverlay = true;
    },

    closeShareOverlay() {
      this.showShareOverlay = false;
    },

    openOptionsModal() {
      this.optionsModalActive = true;
    },

    closeOptionsModal() {
      this.optionsModalActive = false;
    },

    toggleAutoplay() {
      this.$emit('toggle-autoplay');
    },

    unixToDateFormat(value) {
      if (!value) return '';
      return formatMonthDayYear(value);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../assets/_helpers';
@import '../assets/variables-v2';

.player-info {
  overflow: hidden;
  max-width: 100%;
  &__text {
    font-family: Roboto, sans-serif;
    &--light {
      color: #6e6e7a;
      font-weight: 400;
      margin-right: 9px;
      text-decoration: none;
      font-size: 0.875rem;
    }
    &--title {
      white-space: nowrap;
      font-weight: 700;
      font-size: 1rem;
      line-height: 24px;
      display: block;
    }
  }

  &__live-text {
    text-transform: capitalize;
    font-family: Roboto, sans-serif;
    margin-right: 9.5px;
    color: #6e6e7a;
    font-size: 14px;
  }

  &__content {
    padding: 0.25rem 0;
  }

  &__text-scroll {
    animation-name: marqueeText;
    animation-duration: 15s;
    animation-iteration-count: infinite;
    animation-timing-function: linear;
    animation-fill-mode: forwards;
    &:hover {
      animation-play-state: paused;
    }

    &--reset {
      animation-name: marqueeReset;
    }

    @keyframes marqueeText {
      0% {
        transform: translateX(0);
      }
      45% {
        transform: translateX(-100%);
      }
      45.001% {
        transform: translateX(100%);
      }
      90% {
        transform: translateX(0%);
      }
    }

    @keyframes marqueeReset {
      0% {
        transform: translateX(0);
      }
    }
  }

  &__row {
    display: flex;
    justify-content: flex-start;
  }

  &__list {
    margin: 0 -0.5rem;
    padding: 0;
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }

  &__list-item {
    padding: 0 0.5rem;
    list-style: none;
  }

  &__button {
    border: none;
    background: none;
    width: 15px;
    height: 16.6px;
    display: flex;
    justify-content: center;
    align-items: center;

    // R11 (a11y): these share/options glyphs render 15x16.6. Their list items are
    // only 31px apart centre-to-centre (15px + 2 x 0.5rem padding), so a 44px hit
    // box would OVERLAP the neighbour and steal its taps — worse than a small
    // target. Expanded to the largest box that stays clear of the neighbour
    // (30px: 15px each side of centre against a 31px pitch). Height is capped at
    // 24px for the same reason on the other axis — the scrubber sits immediately
    // below, and a taller box would reach into a DRAG control, where a stray tap
    // seeks the audio. At 15px with a 31px pitch these already satisfied WCAG
    // 2.5.8 AA via the spacing exception; 30x24 makes them comfortably tappable
    // (and clears the 24px AA floor outright) without moving anything.
    // @media(max-width)-gated ONLY.
    @media (max-width: 480px) {
      position: relative;

      &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 30px;
        height: 24px;
      }
    }
    &:hover {
      cursor: pointer;
    }
  }
}

.autoplay {
  &__header {
    display: flex;
    align-items: center;
  }

  &__heading {
    display: inline-block;
    margin-right: 1.5rem;
    font-size: 1.25rem;
    font-family: $font;
  }

  &__button {
    display: flex;
    align-items: center;
    width: 52px;
    height: 33px;
    border-radius: 1.85em;
    border: 2px solid #6e6e7a;
    background: #fff;
    padding: 0 0.15rem;
    cursor: pointer;
    &--active {
      justify-content: flex-end;
    }
  }

  &__button-toggle {
    background: #e5e5e5;
    width: 24px;
    height: 25px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    &--active {
      background: #73b657;
    }
  }
}
</style>
